import {
	faCircleExclamation,
	faEnvelope,
	faPhone,
} from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type { UturnNoteDTO } from '@uturn/api/v1';
import {
	getGetEventsByShipmentNumberQueryKey,
	UturnNoteDTOUturnNoteType,
	useGetCarrierContactByShipmentNumber,
	useRegisterShipperMessageToCarrier,
} from '@uturn/api/v1';
import { queryClient } from '@uturn/portal/query';
import {
	Button,
	Callout,
	CalloutIcon,
	CalloutTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
	Icon,
	SheetClose,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	sonner,
	Textarea,
} from '@uturn/ui-kit';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CharacterHint } from './character-hint';
import {
	MAX_UTURN_NOTE_LENGTH,
	type MessageForm,
	messageSchema,
} from './message-carrier-schema';

export function MessageCarrier({ shipmentNumber }: { shipmentNumber: number }) {
	const form = useForm<MessageForm>({
		resolver: zodResolver(messageSchema),
		mode: 'onChange',
		defaultValues: { uturnNote: '' },
	});
	const closeRef = useRef<HTMLButtonElement>(null);
	const { t } = useTranslation();

	const {
		formState: { isValid },
	} = form;

	const { data: carrierDetails } =
		useGetCarrierContactByShipmentNumber(shipmentNumber);

	const hasContactDetails = !!(
		carrierDetails?.data.contactEmail || carrierDetails?.data.contactPhone
	);
	const hasGeneralDetails = !!(
		carrierDetails?.data.organisationEmail ||
		carrierDetails?.data.organisationPhone
	);

	const sendMessage = useRegisterShipperMessageToCarrier({
		mutation: {
			// Defined on the hook (not passed to mutate) so they still fire even if
			// the sheet/component unmounts before the request settles.
			onSuccess: () => {
				sonner.success(
					t(
						'pages.order.shipment-details.assigned-carrier.contact.dm.submit_success',
						'Message sent to carrier',
					),
				);

				queryClient.invalidateQueries({
					queryKey: getGetEventsByShipmentNumberQueryKey(shipmentNumber),
				});
				form.reset();

				// Close the sheet only after a successful send.
				closeRef.current?.click();
			},
			onError: () => {
				sonner.error(
					t(
						'pages.order.shipment-details.assigned-carrier.contact.dm.submit_error',
						'Message could not be send to carrier',
					),
				);
			},
		},
	});

	const onSubmit = (data: MessageForm) => {
		const toSendData: UturnNoteDTO = {
			requestedFor: shipmentNumber,
			sendMessageToCarrier: true,
			sendMessageToShipper: false,
			uturnNote: data.uturnNote,
			uturnNoteType: UturnNoteDTOUturnNoteType.SHIPMENT,
			visibleToCarrier: true,
			visibleToShipper: true,
		};

		sendMessage.mutate({ data: toSendData });
	};

	return (
		<>
			{/* Hidden close target, triggered programmatically on successful send. */}
			<SheetClose ref={closeRef} className="hidden" />
			<SheetHeader>
				<SheetTitle>
					{t(
						'pages.order.shipment-details.assigned-carrier.contact.heading',
						'Contact carrier',
					)}
				</SheetTitle>
				<SheetDescription>
					{t(
						'pages.order.shipment-details.assigned-carrier.contact.subheading',
						'Contact the carrier via phone or email or send a direct message via the Uturn platform.',
					)}
				</SheetDescription>
			</SheetHeader>

			<div className="grid grid-cols-1 gap-x-6 gap-y-4 pt-4 sm:max-w-xl sm:grid-cols-6">
				<div className="col-span-full flex flex-col gap-1">
					<h3 className="text font-heading font-medium tracking-tight">
						{t(
							'pages.order.shipment-details.assigned-carrier.contact.details.heading',
							'Contact details',
						)}
					</h3>
					{hasContactDetails && (
						<div className="flex flex-col gap-1">
							<h6 className="block text-sm font-medium leading-none">
								{t(
									'pages.order.shipment-details.assigned-carrier.contact.details.primary',
									'Primary contact',
								)}
							</h6>
							<ul className="flex flex-col gap-1">
								{carrierDetails?.data.contactPhone && (
									<li className="flex items-center gap-2">
										<Icon
											icon={faPhone}
											className="text-secondary-600 size-3.5"
										/>
										<a
											href={`tel:${carrierDetails?.data.contactPhone}`}
											className="text-primary-600 text-sm"
										>
											{carrierDetails?.data.contactPhone}
										</a>
									</li>
								)}
								{carrierDetails?.data.contactEmail && (
									<li className="flex items-center gap-2">
										<Icon
											icon={faEnvelope}
											className="text-secondary-600 size-3.5"
										/>
										<a
											href={`mailto:${carrierDetails?.data.contactEmail}`}
											className="text-primary-600 text-sm"
										>
											{carrierDetails?.data.contactEmail}
										</a>
									</li>
								)}
							</ul>
						</div>
					)}
					{hasGeneralDetails && (
						<div className="mt-1 flex flex-col gap-1">
							<h6 className="text-secondary-900 block text-sm font-medium leading-6">
								{t(
									'pages.order.shipment-details.assigned-carrier.contact.details.general',
									'General',
								)}
							</h6>
							<ul className="flex flex-col gap-1">
								{carrierDetails?.data.organisationPhone && (
									<li className="flex items-center gap-2">
										<Icon
											icon={faPhone}
											className="text-secondary-600 size-3.5"
										/>
										<a
											href={`tel:${carrierDetails?.data.organisationPhone}`}
											className="text-primary-600 text-sm"
										>
											{carrierDetails?.data.organisationPhone}
										</a>
									</li>
								)}
								{carrierDetails?.data.organisationEmail && (
									<li className="flex items-center gap-2">
										<Icon
											icon={faEnvelope}
											className="text-secondary-600 size-3.5"
										/>
										<a
											href={`mailto:${carrierDetails?.data.organisationEmail}`}
											className="text-primary-600 text-sm"
										>
											{carrierDetails?.data.organisationEmail}
										</a>
									</li>
								)}
							</ul>
						</div>
					)}
				</div>
				<div className="col-span-full flex flex-col gap-1">
					<h3 className="text font-heading font-medium tracking-tight">
						{t(
							'pages.order.shipment-details.assigned-carrier.contact.dm.heading',
							'Direct message',
						)}
					</h3>
					<p className="pb-2 text-sm leading-6">
						{t(
							'pages.order.shipment-details.assigned-carrier.contact.dm.subheading',
							'Your message will be send to the carrier and stored in the event log.',
						)}
					</p>
					<Form {...form}>
						<form
							onSubmit={(e) => {
								e.stopPropagation();
								return form.handleSubmit(onSubmit)(e);
							}}
							className="space-y-4"
						>
							<FormField
								control={form.control}
								name="uturnNote"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Textarea
												{...field}
												rows={6}
												maxLength={MAX_UTURN_NOTE_LENGTH}
											/>
										</FormControl>
										<CharacterHint
											currentCount={field.value?.length ?? 0}
											maxCharacters={MAX_UTURN_NOTE_LENGTH}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Callout className="gap-3 p-3">
								<CalloutIcon>
									<Icon icon={faCircleExclamation} />
								</CalloutIcon>
								<CalloutTitle className="text-xs leading-5">
									{t(
										'pages.order.shipment-details.assigned-carrier.contact.dm.disclaimer',
										'These contact details are intended for communication about this order. Collaborations that arise through UTURN also run through UTURN.',
									)}
								</CalloutTitle>
							</Callout>
							<div className="flex justify-end sm:col-span-full">
								<Button
									type="submit"
									size="sm"
									disabled={!isValid || sendMessage.isPending}
								>
									{t(
										'pages.order.shipment-details.assigned-carrier.contact.dm.submit',
										'Send message',
									)}
								</Button>
							</div>
						</form>
					</Form>
				</div>
			</div>
		</>
	);
}
